/**
 * Action Buttons Component
 *
 * Provides Buy, Receive, and Send action buttons for the dashboard
 */

import React from "react";
import { useNavigation } from "@/adapters/navigation";

interface ActionButtonsProps {
  /** Hide the Send button (e.g., on Smart Assets tab where transfers are in detail view) */
  hideSend?: boolean;
  /** Optional parent-provided Buy behavior for the active asset selector */
  onBuyClick?: () => void;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({ hideSend = false, onBuyClick }) => {
  const navigation = useNavigation();

  const handleBuyClick = () => {
    if (onBuyClick) {
      onBuyClick();
      return;
    }

    navigation.push("/satchel/buy");
  };

  const handleReceiveClick = () => {
    navigation.push("/satchel/receive");
  };

  const handleSendClick = () => {
    navigation.push("/satchel/send");
  };

  return (
    <div className={`grid ${hideSend ? "grid-cols-2" : "grid-cols-3"} gap-3 my-6`}>
      {/* Buy Button */}
      <button
        onClick={handleBuyClick}
        className="flex flex-col items-center justify-center p-5 bg-sa-surface/80 border border-sa-border rounded-xl hover:bg-sa-surface-hover hover:border-sa-success/50 transition-all duration-200 shadow-lg"
      >
        <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mb-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M12 4v16m8-8H4"
            />
          </svg>
        </div>
        <span className="text-base font-semibold text-sa-text">Buy</span>
      </button>

      {/* Receive Button */}
      <button
        onClick={handleReceiveClick}
        className="flex flex-col items-center justify-center p-5 bg-sa-surface/80 border border-sa-border rounded-xl hover:bg-sa-surface-hover hover:border-primary/50 transition-all duration-200 shadow-lg"
      >
        <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mb-3 shadow-lg shadow-blue-500/20">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
        <span className="text-base font-semibold text-sa-text">Receive</span>
      </button>

      {/* Send Button - hidden on Smart Assets tab where transfers are in detail view */}
      {!hideSend && (
        <button
          onClick={handleSendClick}
          className="flex flex-col items-center justify-center p-5 bg-sa-surface/80 border border-sa-border rounded-xl hover:bg-sa-surface-hover hover:border-sa-warning/50 transition-all duration-200 shadow-lg"
        >
          <div className="w-14 h-14 rounded-full bg-gold flex items-center justify-center mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
          </div>
          <span className="text-base font-semibold text-sa-text">Send</span>
        </button>
      )}
    </div>
  );
};
